import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Divider, Flex, Input, InputRef, Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ResourceType } from '@/services/database';
import { FavoriteStatusIcon } from '@/shared/components/icons';
import { ButtonSkeleton } from '@/shared/components/skeletons';
import {
    getWorkspaceResourceStatusActionLabel,
    WorkspaceResourceStatusIndicator,
} from '@/shared/components/workspace-resources';
import { SubjectReference } from '@/types';

import PreviousNextResourceButtonGroup from '../../../buttons/PreviousNextResourceButtonGroup';
import InviteToResourceDropdown, {
    type InviteableResourceVisibility,
    type InviteableWorkspaceResource,
} from '../../../dropdowns/InviteToResourceDropdown';
import ActiveAvatars from '../../../groups/ActiveAvatars';
import type { CompactActiveAvatarCollaborator } from '../../../groups/CompactActiveAvatarGroup';
import { ResourceHeaderTags } from '../../../tags';
import SmallTooltip from '../../../tooltips/SmallTooltip';
import ResourceHeader, { ResourceHeaderBreadcrumbContent } from './ResourceHeaderBase';

type ResourceHeaderToggleAction = {
    active?: boolean;
    activeTooltip?: ReactNode;
    disabled?: boolean;
    inactiveTooltip?: ReactNode;
    onClick?: () => void;
    showWhenInactive?: boolean;
};

type ResourceHeaderTitleAvatar = {
    icon?: ReactNode;
    src?: string | false | null;
};

type NavigableResource = {
    id?: string | number;
};

interface ResourceHeaderTitleProps {
    avatar?: ResourceHeaderTitleAvatar;
    editable?: {
        ariaLabel?: string;
        disabled?: boolean;
        maxLength?: number;
        onSave: (title: string) => Promise<void> | void;
    };
    fallbackWidth?: number;
    title?: ReactNode;
}

interface ResourceHeaderStatusActionsProps {
    archive?: ResourceHeaderToggleAction;
    favorite?: ResourceHeaderToggleAction;
    lock?: ResourceHeaderToggleAction;
    syncStatus?: ReactNode;
    tags?: {
        disabled?: boolean;
        resourceLabel: string;
        subject: SubjectReference;
    };
}

interface ResourceHeaderNavigationProps {
    getResourcePath?: (resource: NavigableResource) => string | undefined;
    pluralResourceLabel: string;
    resource: NavigableResource;
    resources: NavigableResource[];
    singularResourceLabel: string;
}

interface ResourceHeaderCollaboratorsProps {
    collaborators?: readonly CompactActiveAvatarCollaborator[];
    loading?: boolean;
    onVisibilityChange?: (visibility: InviteableResourceVisibility) => Promise<void> | void;
    resource?: InviteableWorkspaceResource;
    resourceId?: string;
    resourceType?: ResourceType;
}

const ResourceHeaderTitle: FC<ResourceHeaderTitleProps> = ({
    avatar,
    editable,
    fallbackWidth = 16 * 10,
    title,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const inputRef = useRef<InputRef>(null);
    const titleButtonRef = useRef<HTMLButtonElement>(null);
    const cancelingRef = useRef(false);
    const savingRef = useRef(false);
    const [editing, setEditing] = useState(false);
    const [inputWidth, setInputWidth] = useState(fallbackWidth);
    const [saving, setSaving] = useState(false);
    const stringTitle = typeof title === 'string' ? title : '';
    const [value, setValue] = useState('');
    const canEdit = Boolean(editable && !editable.disabled && stringTitle);

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus({ cursor: 'end' });
        }
    }, [editing]);

    const cancelEditing = () => {
        cancelingRef.current = true;
        setValue(stringTitle);
        setEditing(false);
        queueMicrotask(() => {
            cancelingRef.current = false;
        });
    };

    const saveTitle = async () => {
        if (!editable || cancelingRef.current || savingRef.current) {
            return;
        }

        const nextTitle = value.trim();

        if (!nextTitle || nextTitle === stringTitle.trim()) {
            cancelEditing();
            return;
        }

        savingRef.current = true;
        setSaving(true);

        try {
            await editable.onSave(nextTitle);
            setValue(nextTitle);
        } catch (error) {
            captureException(error);
            setValue(stringTitle);
        } finally {
            savingRef.current = false;
            setSaving(false);
            setEditing(false);
        }
    };

    return (
        <Flex align='center'>
            {avatar && (
                <Avatar
                    size='small'
                    shape='square'
                    style={{
                        marginRight: '0.5rem',
                        border: `1px solid ${token.colorBorder}`,
                        backgroundColor: token.colorBgContainer,
                    }}
                    icon={avatar.icon}
                    src={avatar.src || undefined}
                />
            )}
            {editing ? (
                <span style={{ display: 'inline-flex', minWidth: 0 }}>
                    <Input
                        ref={inputRef}
                        allowClear={false}
                        aria-label={editable?.ariaLabel ?? 'Rename resource'}
                        disabled={saving}
                        maxLength={editable?.maxLength ?? 250}
                        onBlur={() => void saveTitle()}
                        onChange={(event) => setValue(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                event.preventDefault();
                                cancelEditing();
                            }
                        }}
                        onPressEnter={(event) => event.currentTarget.blur()}
                        style={{
                            background: 'transparent',
                            border: 0,
                            borderRadius: 0,
                            boxShadow: 'none',
                            color: token.colorText,
                            font: 'inherit',
                            fontWeight: 500,
                            height: '1.375rem',
                            lineHeight: '1.375rem',
                            margin: 0,
                            maxWidth: '24rem',
                            padding: 0,
                            width: inputWidth,
                        }}
                        value={value}
                        variant='borderless'
                    />
                </span>
            ) : title ? (
                canEdit ? (
                    <button
                        ref={titleButtonRef}
                        aria-label={editable?.ariaLabel ?? `Rename ${stringTitle}`}
                        onClick={() => {
                            const renderedWidth = titleButtonRef.current?.getBoundingClientRect().width ?? 0;
                            const fallbackTitleWidth = Math.max(stringTitle.length, 1) * token.fontSize * 0.55;

                            setInputWidth(
                                Math.min(Math.max(renderedWidth || fallbackTitleWidth, token.fontSize * 2), 16 * 24),
                            );
                            setValue(stringTitle);
                            setEditing(true);
                        }}
                        style={{
                            background: 'transparent',
                            border: 0,
                            color: token.colorText,
                            cursor: 'text',
                            font: 'inherit',
                            fontWeight: 500,
                            lineHeight: '1.375rem',
                            maxWidth: '24rem',
                            overflow: 'hidden',
                            padding: 0,
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        type='button'
                    >
                        {title}
                    </button>
                ) : (
                    <Typography.Text>{title}</Typography.Text>
                )
            ) : (
                <ButtonSkeleton size='small' style={{ width: fallbackWidth }} />
            )}
        </Flex>
    );
};

const ResourceHeaderStatusActions: FC<ResourceHeaderStatusActionsProps> = ({
    archive,
    favorite,
    lock,
    syncStatus,
    tags,
}): JSX.Element => {
    return (
        <Flex align='center'>
            {favorite && (
                <SmallTooltip
                    title={
                        favorite.active
                            ? (favorite.activeTooltip ?? 'Remove from Favorites')
                            : (favorite.inactiveTooltip ?? 'Add to Favorites')
                    }
                    placement='right'
                    padding='0 0.25rem'
                >
                    <Button
                        onClick={favorite.onClick}
                        disabled={favorite.disabled}
                        size='small'
                        type='text'
                        style={{ marginTop: '-1px' }}
                    >
                        <FavoriteStatusIcon favorited={Boolean(favorite.active)} />
                    </Button>
                </SmallTooltip>
            )}
            {tags && <ResourceHeaderTags {...tags} />}
            {syncStatus}
            {lock && (lock.active || lock.showWhenInactive) && (
                <SmallTooltip
                    title={
                        lock.active
                            ? (lock.activeTooltip ?? getWorkspaceResourceStatusActionLabel('locked', true))
                            : (lock.inactiveTooltip ?? getWorkspaceResourceStatusActionLabel('locked', false))
                    }
                    placement='right'
                    padding='0 0.25rem'
                >
                    <Button
                        aria-label={getWorkspaceResourceStatusActionLabel('locked', Boolean(lock.active))}
                        icon={
                            <WorkspaceResourceStatusIndicator
                                status='locked'
                                style={{ marginInlineStart: 0 }}
                                tooltip={false}
                            />
                        }
                        size='small'
                        type='text'
                        style={{
                            marginLeft: '0.5rem',
                            marginTop: '-1px',
                        }}
                        onClick={lock.onClick}
                        disabled={lock.disabled}
                    />
                </SmallTooltip>
            )}
            {archive && (archive.active || archive.showWhenInactive) && (
                <SmallTooltip
                    title={
                        archive.active
                            ? (archive.activeTooltip ?? getWorkspaceResourceStatusActionLabel('archived', true))
                            : (archive.inactiveTooltip ?? getWorkspaceResourceStatusActionLabel('archived', false))
                    }
                    placement='right'
                    padding='0 0.25rem'
                >
                    <Button
                        aria-label={getWorkspaceResourceStatusActionLabel('archived', Boolean(archive.active))}
                        icon={
                            <WorkspaceResourceStatusIndicator
                                status='archived'
                                style={{ marginInlineStart: 0 }}
                                tooltip={false}
                            />
                        }
                        size='small'
                        type='text'
                        style={{
                            marginLeft: '0.5rem',
                            marginTop: '-1px',
                        }}
                        onClick={archive.onClick}
                        disabled={archive.disabled}
                    />
                </SmallTooltip>
            )}
        </Flex>
    );
};

const ResourceHeaderNavigation: FC<ResourceHeaderNavigationProps> = ({
    getResourcePath,
    pluralResourceLabel,
    resource,
    resources,
    singularResourceLabel,
}): JSX.Element | null => {
    if (!resource?.id || !resources?.length) {
        return null;
    }

    return (
        <PreviousNextResourceButtonGroup
            showCount
            resource={resource}
            resources={resources}
            singularResourceLabel={singularResourceLabel}
            pluralResourceLabel={pluralResourceLabel}
            getResourcePath={getResourcePath}
        />
    );
};

const ResourceHeaderCollaborators: FC<ResourceHeaderCollaboratorsProps> = ({
    collaborators,
    loading,
    onVisibilityChange,
    resource,
    resourceId,
    resourceType,
}): JSX.Element => (
    <Flex align='center' gap='1rem'>
        {loading ? (
            <ButtonSkeleton />
        ) : collaborators?.length ? (
            <ActiveAvatars collaborators={collaborators} resourceId={resourceId ?? resource?.id} />
        ) : null}
        {resource && resourceType ? (
            <InviteToResourceDropdown
                resource={resource}
                resourceType={resourceType}
                onVisibilityChange={onVisibilityChange}
            />
        ) : null}
    </Flex>
);

const ResourceHeaderDivider: FC = (): JSX.Element => <Divider type='vertical' />;

export {
    ResourceHeaderBreadcrumbContent,
    ResourceHeaderCollaborators,
    ResourceHeaderDivider,
    ResourceHeaderNavigation,
    ResourceHeaderStatusActions,
    ResourceHeaderTitle,
};

export default ResourceHeader;
